import {useState } from "react";
import commentIcon from "../../../assets/icons/commentIcon.svg";
import Input from "../../../components/input";
import arrow from "../../../assets/icons/arrow.svg";
import Button from "../../../components/button";
import { useCampaignComments } from "../../reuseableEffects";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { SendComment } from "../../../hooks/local/reducer";
import { showErrorMessage, showSuccessMessage } from "../../../hooks/constants";

import ReCAPTCHA from "react-google-recaptcha";

const CampaignComments = ({ campaignReference }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const comments = useCampaignComments(campaignReference);

  const dispatch = useDispatch()
  const loading = useSelector((state) => state.user.loading);

  const commentsPerPage = 5;
  const totalPages = Math.ceil(comments.length / commentsPerPage);
  const startIndex = (currentPage - 1) * commentsPerPage;
  const endIndex = startIndex + commentsPerPage;
  const currentComments = comments.slice().reverse().slice(startIndex, endIndex);

  // Google captcha settings
  const onChange = (token) => {
    setRecaptchaToken(token)
  };

  // Function to format the date as "4th November 2023"
const formatDate = (dateStr) => {
  const dateObj = new Date(dateStr);

  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return "th"; // For 11th - 20th
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const day = dateObj.getDate();
  const daySuffix = getDaySuffix(day);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();

  return `${day}${daySuffix} ${month}, ${year}`;
};

  const sendCommentForm = useFormik({
    initialValues:{
      message: "",
      campaign_reference: "",
      user_name: "",
    },
    validationSchema: Yup.object({
      message: Yup.string().required("Your comment is required"),
      user_name: Yup.string().required("Your name is required"),
    }),
    onSubmit: async (values, {resetForm}) => {
      if (!recaptchaToken) {
        showErrorMessage("Please complete the reCAPTCHA");
        return;
      }
      const {message, user_name,} = values;
      let sendCommentData = { campaign_reference:campaignReference, message, user_name, recaptcha_token: recaptchaToken}
      // console.log(sendCommentData)
      const payload = await dispatch(SendComment(sendCommentData));
      // console.log(payload.payload);
      if(payload.payload.status_code === "0"){
        showSuccessMessage(payload.payload.message)
        resetForm();
        window.location.reload();
      } else { showErrorMessage(payload.payload.message)}
    }
  });

  return (
    <div className="py-6 border-y">
      <div className="mb-4 flex gap-2 items-center">
        <img src={commentIcon} alt="" className="h-[18px]" />
        <p className="font-medium text-secondary">Comments:</p>
      </div>
      {currentComments.length === 0 ? (
        <div>There are no comments on this campaign yet</div>
      ) : (
        <div>
          <div className="grid gap-4">
            {currentComments.map((comment, index) => (
              <div
                key={index}
                className="pb-4 last:pb-0 border-b last:border-0"
              >
                <div className="flex gap-2 text-secondary">
                  <span>{comment.user_name}</span>
                  <span>-</span>
                  <span>{formatDate(comment.time_in)}</span>
                </div>
                <p>{comment.message}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-4">
            <span
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              className="border p-2 rounded bg-white"
            >
              <img
                src={arrow}
                alt="previous"
                className={`${
                  currentPage === 1
                    ? "opacity-20"
                    : "opacity-100 cursor-pointer"
                } h-4 rotate-90`}
              />
            </span>
            <div className="text-sm">
              Page {currentPage} of {totalPages}
            </div>
            <span
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              className="border p-2 rounded bg-white"
            >
              <img
                src={arrow}
                alt="next"
                className={`${
                  currentPage === totalPages
                    ? "opacity-20"
                    : "opacity-100 cursor-pointer"
                } h-4 -rotate-90`}
              />
            </span>
          </div>
        </div>
      )}

      {/* Add a comment */}
      <form onSubmit={sendCommentForm.handleSubmit} className="mt-6 grid gap-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label={"Your name:"}
            name={"user_name"}
            value={sendCommentForm.values.user_name}
            onChange={sendCommentForm.handleChange}
            onBlur={sendCommentForm.handleBlur}
            onError={
              sendCommentForm.touched.user_name &&
              sendCommentForm.errors.user_name
                ? sendCommentForm.errors.user_name
                : null
            }
          />
          <div className="md:col-span-2">
            <Input
              label={"Comment:"}
              placeholder={"Add a comment..."}
              name={"message"}
              value={sendCommentForm.values.message}
              onChange={sendCommentForm.handleChange}
              onBlur={sendCommentForm.handleBlur}
              onError={
                sendCommentForm.touched.message &&
                sendCommentForm.errors.message
                  ? sendCommentForm.errors.message
                  : null
              }
            />
          </div>
        </div>
        <ReCAPTCHA sitekey="6Lcsw1EqAAAAAGlwTlQSTU3-GB-LawbvmXS0UjAq" onChange={onChange} />
        <Button
          buttonText={"Send comment"}
          otherStyles={"bg-black text-white"}
          loading={loading}
        />
      </form>
    </div>
  );
};

export default CampaignComments;
